import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

import { OrbitAI } from "@/components/OrbitAI";

export default function AiChatBubble() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "ai", text: "Bonjour ! Je suis Orbit AI. Posez-moi une question sur vos KPI, corners, missions… ✨" },
  ]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = q.trim(); if (!text || loading) return;
    setMsgs(m => [...m, { role: "me", text }]); setQ(""); setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMsgs(m => [...m, { role: "ai", text: data.reply }]);
    } catch {
      setMsgs(m => [...m, { role: "ai", text: "Désolé, une erreur est survenue." }]);
    } finally { setLoading(false); }
  };

  const suggestions = [
    "Quels corners sont les plus en difficulté ?",
    "Résume la performance de la semaine",
    "Quels magasins ont un contrat qui expire bientôt ?",
  ];

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button data-testid="ai-chat-bubble" onClick={() => setOpen(true)}
                className="fixed z-[999] bottom-5 right-5 w-14 h-14 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 text-white shadow-lg shadow-purple-900/30 hover:scale-105 transition grid place-items-center">
          <MessageCircle className="w-6 h-6"/>
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-400 border-2 border-white rounded-full"/>
        </button>
      )}
      {/* Chat panel */}
      {open && (
        <div className="fixed z-[999] bottom-5 right-5 w-[92vw] max-w-sm h-[560px] max-h-[85vh] rounded-2xl bg-white shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-3 flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-white/20 grid place-items-center"><OrbitAI className="w-4 h-4"/></div>
            <div className="flex-1">
              <div className="font-semibold text-sm">Orbit AI</div>
              <div className="text-xs text-white/80">Assistant conversationnel · GPT</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ai-chat-close"
                    className="w-8 h-8 grid place-items-center rounded-lg hover:bg-white/10"><X className="w-4 h-4"/></button>
          </div>
          <div ref={boxRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "me" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${
                  m.role === "me" ? "bg-blue-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl px-3 py-2 text-sm text-slate-500 flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin"/> Réflexion…
                </div>
              </div>
            )}
            {msgs.length <= 1 && !loading && (
              <div className="pt-2 space-y-1.5">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 px-1">Suggestions</div>
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => { setQ(s); }} data-testid={`ai-chat-sugg-${i}`}
                          className="w-full text-left text-xs px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-blue-400 hover:text-blue-600 text-slate-600 transition">
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          <form onSubmit={send} className="p-2 border-t border-slate-200 bg-white flex items-center gap-1.5">
            <input data-testid="ai-chat-input" value={q} onChange={e => setQ(e.target.value)}
                   placeholder="Posez votre question…" className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-purple-100"/>
            <button data-testid="ai-chat-send" type="submit" disabled={loading || !q.trim()}
                    className="w-10 h-10 grid place-items-center rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 text-white disabled:opacity-40">
              <Send className="w-4 h-4"/>
            </button>
          </form>
        </div>
      )}
    </>
  );
}
